/**
 * Componente principal de la aplicación
 * Configura el enrutamiento y los providers globales
 * 
 * VERSIÓN INMERSIVA - Diseño tipo Awwwards/Heinz
 * con scrollytelling, motion UI y tipografía gigante
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { CookieProvider } from './context/CookieContext';
import CookieBanner from './components/cookies/CookieBanner';
import ScrollToTop from './components/common/ScrollToTop';

// Importar páginas inmersivas
import HomePage from './pages/HomePageImmersive';
import AboutPage from './pages/AboutPageImmersive';
import ProductsPage from './pages/ProductsPageImmersive';
import RetailProductsPage from './pages/RetailProductsPageImmersive';
import HorecaProductsPage from './pages/HorecaProductsPageImmersive';
import RecipesPage from './pages/RecipesPageImmersive';
import RecipeDetailPage from './pages/RecipeDetailPageImmersive';

// Importar otras páginas (mantienen diseño anterior por ahora)
import ProductDetailPage from './pages/ProductDetailPage';
import HorecaProductDetailPage from './pages/HorecaProductDetailPage';
import NewsPage from './pages/NewsPage';
import NewsDetailPage from './pages/NewsDetailPage';
import ContactPage from './pages/ContactPage';
import TransparencyPage from './pages/TransparencyPage';
import OrganizativeInfoPage from './pages/transparency/OrganizativeInfoPage';
import OrganizationalInfoPage from './pages/transparency/OrganizationalInfoPage';
import InstitutionalInfoPage from './pages/transparency/InstitutionalInfoPage';
import EconomicInfoPage from './pages/transparency/EconomicInfoPage';
import ContractsInfoPage from './pages/transparency/ContractsInfoPage';
import SubsidiesInfoPage from './pages/transparency/SubsidiesInfoPage';
import ServiciosProcedimientosPage from './pages/transparency/ServiciosProcedimientosPage';
import CookiesPage from './pages/CookiesPage';
import PrivacyPage from './pages/PrivacyPage';
import LegalPage from './pages/LegalPage';

/**
 * Componente raíz de la aplicación
 * Configura el Router y los Providers necesarios
 * 
 * NOTA: La HomePage usa el nuevo diseño inmersivo.
 * Las demás páginas se irán actualizando progresivamente.
 * 
 * @returns {JSX.Element} Aplicación completa
 */
function App() {
  return (
    <CookieProvider>
      <Router>
        {/* Scroll al inicio en cada cambio de ruta */}
        <ScrollToTop />
        
        {/* Rutas de la aplicación */}
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/recetas" element={<RecipesPage />} />
          <Route path="/recetas/:slug" element={<RecipeDetailPage />} />
          {/* Alias de la ruta del formulario de envío de recetas (destino del QR impreso).
              Renderiza la misma página de recetas; el modal se abre solo al detectar esta ruta. */}
          <Route path="/envio-receta" element={<RecipesPage />} />
          <Route path="/nosotros" element={<AboutPage />} />
          <Route path="/sobre-nosotros" element={<AboutPage />} />
          <Route path="/cookies" element={<CookiesPage />} />
          <Route path="/privacidad" element={<PrivacyPage />} />
          <Route path="/aviso" element={<LegalPage />} />
          
          {/* Rutas de productos */}
          <Route path="/productos" element={<ProductsPage />} />
          <Route path="/productos/retail" element={<RetailProductsPage />} />
          <Route path="/productos/retail/:productId" element={<ProductDetailPage />} />
          <Route path="/productos/horeca" element={<HorecaProductsPage />} />
          <Route path="/productos/horeca/:productId" element={<HorecaProductDetailPage />} />
          
          {/* Rutas de noticias */}
          <Route path="/noticias" element={<NewsPage />} />
          <Route path="/noticias/:slug" element={<NewsDetailPage />} />
          
          {/* Ruta de contacto */}
          <Route path="/contacto" element={<ContactPage />} />
          
          {/* Ruta de transparencia */}
          <Route path="/transparencia" element={<TransparencyPage />} />
          <Route path="/transparencia/organizativa" element={<OrganizationalInfoPage />} />
          <Route path="/transparencia/normativa" element={<OrganizativeInfoPage />} />
          <Route path="/transparencia/acceso" element={<InstitutionalInfoPage />} />
          <Route path="/transparencia/economica" element={<EconomicInfoPage />} />
          <Route path="/transparencia/contratos" element={<ContractsInfoPage />} />
          <Route path="/transparencia/ayudas" element={<SubsidiesInfoPage />} />
          <Route path="/transparencia/servicios" element={<ServiciosProcedimientosPage />} />
        </Routes>

        {/* Banner de cookies global */}
        <CookieBanner />
      </Router>
    </CookieProvider>
  );
}

export default App;
